import React from 'react';
import { useTranslate } from '@bynder/localization';
import { useEllipsedTooltip } from '../../helpers/hooks';
import Tooltip from '../shared/Tooltip';
import { addQuotes, removeDot } from '../guides/Inspector/helpers';
import { CSS_CLASSES } from '../guides/Inspector/constants';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { getTypographyWeight } from '../shared/styled-components/constants';

interface Props {
  label?: string;
  code: string;
  onChangeCode?: (
    e: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number,
    code: string,
  ) => void;
  readOnly?: boolean;
  row?: number;
  onDeleteRow?: (rowIndex: number) => void;
  $modeStyles: {
    fontFamily: string;
    fontSize: number;
    fontStyle: string;
    color: string;
  };
}

export const CustomColorSwatchKey: React.FC<Props> = ({
  label,
  code,
  onChangeCode = null,
  readOnly,
  row,
  onDeleteRow,
  $modeStyles,
}) => {
  const { translate } = useTranslate();
  const inputRef = React.createRef<HTMLInputElement>();
  const { hasEllipsedName, tooltipRef } = useEllipsedTooltip();

  const onBlurFn = () => {
    if (!inputRef.current.value.length && !code.length) {
      onDeleteRow(row);
    }
  };

  if (readOnly) {
    return hasEllipsedName ? (
      <Tooltip label={label}>
        <ModeTitle $modeStyles={$modeStyles} ref={tooltipRef}>
          {label}
        </ModeTitle>
      </Tooltip>
    ) : (
      <ModeTitle
        $modeStyles={$modeStyles}
        className={removeDot(CSS_CLASSES.COLOR_PALETTE_KEY)}
        ref={tooltipRef}
      >
        {label}
      </ModeTitle>
    );
  }

  return (
    <StyledInput
      $modeStyles={$modeStyles}
      className={removeDot(CSS_CLASSES.COLOR_PALETTE_KEY)}
      ref={inputRef}
      onBlur={onBlurFn}
      value={label}
      onChange={e => onChangeCode(e, row, label)}
      placeholder={translate('CUSTOM_COLOR_SWATCH_KEY.INPUT_PLACEHOLDER')}
    />
  );
};

export const ModeTitle = styled.span<{
  $modeStyles: {
    fontFamily: string;
    fontSize: number;
    fontStyle: string;
    color: string;
  };
}>`
  color: ${props =>
    props.$modeStyles?.color && props.$modeStyles?.color
      ? props.$modeStyles?.color
      : token.gray800a};
  font-family: ${props =>
    props.$modeStyles?.fontFamily && addQuotes(props.$modeStyles?.fontFamily)};
  font-size: ${props =>
    props.$modeStyles?.fontSize && props.$modeStyles?.fontSize}px;
  font-weight: ${props =>
    props.$modeStyles?.fontStyle &&
    getTypographyWeight(
      props.theme,
      props.$modeStyles?.fontFamily,
      props.$modeStyles?.fontStyle,
    )};
`;

const StyledInput = styled.input<{
  $modeStyles: {
    fontFamily: string;
    fontSize: number;
    fontStyle: string;
    color: string;
  };
}>`
  color: ${props =>
    props.$modeStyles?.color && props.$modeStyles?.color
      ? props.$modeStyles?.color
      : token.gray800a};
  font-family: ${props =>
    props.$modeStyles?.fontFamily && addQuotes(props.$modeStyles?.fontFamily)};
  font-size: ${props =>
    props.$modeStyles?.fontSize && props.$modeStyles?.fontSize}px;
  font-weight: ${props =>
    props.$modeStyles?.fontStyle &&
    getTypographyWeight(
      props.theme,
      props.$modeStyles?.fontFamily,
      props.$modeStyles?.fontStyle,
    )};

  &::placeholder {
    color: ${props =>
      props.$modeStyles?.color ? props.$modeStyles?.color : token.gray300a};
  }
`;
